import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import '../../css/projects-create.css';
import { postProject, updateProject } from '../../services/projectsService';
import { toast } from 'react-toastify';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';

const CreateProjectPage = () => {
    const location = useLocation();
    const projectToEdit = location.state?.projectToEdit;

    const [form, setForm] = useState({
        title: projectToEdit?.title || '',
        description: projectToEdit?.description || '',
        start_date: projectToEdit?.start_date || '',
        end_date: projectToEdit?.end_date || '',
        budget: projectToEdit?.budget || '',
        status: projectToEdit?.status || 'ongoing',
        images: [], // For new image uploads
    });

    const [existingImages, setExistingImages] = useState(projectToEdit?.images || []);

    const removeExistingImage = (imageId) => {
        setExistingImages((prev) => {
            const updatedImages = prev.filter((image) => image.id !== imageId);
            return updatedImages; // Update the existing images only
        });
    };

    const removeNewFile = (index) => {
        setForm((prev) => {
            const updatedImages = prev.images.filter((_, i) => i !== index);
            return {
                ...prev,
                images: updatedImages,
            };
        });
    };

    // React-Select options for status
    const options = [
        { value: 'ongoing', label: 'Ongoing' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
    ];

    const getColor = (value) => {
        if (value === 'ongoing') return '#28a745';     // green
        if (value === 'completed') return '#fd7e14';   // orange
        if (value === 'cancelled') return '#dc3545';   // red
        return '#000';
    };

    const customStyles = {
        singleValue: (provided, state) => ({
            ...provided,
            color: getColor(state.data.value),
            fontWeight: 500,
        }),
        option: (provided, state) => ({
            ...provided,
            color: getColor(state.data.value),
            backgroundColor: state.isFocused ? '#f0f0f0' : '#fff',
            fontWeight: 500,
            cursor: 'pointer'
        })
    };

    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const handleStatusChange = (selectedOption) => {
        setForm((prev) => ({
            ...prev,
            status: selectedOption.value,
        }));
    };

    const onDrop = useCallback((acceptedFiles) => {
        setForm((prev) => ({
            ...prev,
            images: [...prev.images, ...acceptedFiles],
        }));
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        multiple: true,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (form.images.length === 0 && !projectToEdit) {
            alert('Please upload at least one image.');
            return;
        }

        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('description', form.description);
        formData.append('start_date', form.start_date);
        formData.append('end_date', form.end_date);
        formData.append('budget', form.budget);
        formData.append('status', form.status);

        // Add new images to the form data
        form.images.forEach((file) => {
            formData.append('images', file);
        });

        // If editing a project, include existing images
        if (projectToEdit) {
            formData.append('existing_image_ids', existingImages.map(image => image.id));
        }

        try {
            let response;
            if (projectToEdit) {
                response = await updateProject(projectToEdit.id, formData);  // Update Project
                toast.success("Project updated successfully!");
            } else {
                response = await postProject(formData);  // Create New Project
                toast.success("Project posted successfully!");
            }

            // Clear form and navigate back
            setForm({
                title: '',
                description: '',
                start_date: '',
                end_date: '',
                budget: '',
                status: 'ongoing',
                images: [],
            });
            navigate(-1);
        } catch (error) {
            console.error('Failed to submit project:', error);
            toast.error('Failed to submit project. Please try again.');
        }
    };

    return (
        <div className='projects-create-main'>
            <h2><i className="fa-solid fa-diagram-project folder-icon-title"></i> {projectToEdit ? 'Edit Project' : 'Create a New Project'}</h2>

            <form onSubmit={handleSubmit} className='project-form'>
                {/* Dropzone */}
                <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
                    <input {...getInputProps()} />
                    <p>{isDragActive ? 'Drop the images here...' : 'Drag and drop images, or click to select'}</p>
                </div>

                {/* Thumbnails */}
                <div className="preview-images">
                    {form.images.map((file, index) => (
                        <div key={index} className="image-thumb-container">
                            <img
                                src={URL.createObjectURL(file)}
                                alt={`preview-${index}`}
                                className="image-thumb"
                            />
                            <button
                                type="button"
                                className="project-image-remove-button"
                                onClick={() => removeNewFile(index)} // Using the updated function
                            >
                                ×
                            </button>
                        </div>
                    ))}

                    {existingImages.map((image) => (
                        <div key={image.id} className="image-thumb-container">
                            <img
                                src={image.image}
                                alt={`existing-preview-${image.id}`}
                                className="image-thumb"
                            />
                            <button
                                type="button"
                                className="project-image-remove-button"
                                onClick={() => removeExistingImage(image.id)}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>

                <input
                    type='text'
                    name='title'
                    value={form.title}
                    onChange={handleChange}
                    placeholder='Project Title'
                    required
                />

                <textarea
                    name='description'
                    value={form.description}
                    onChange={handleChange}
                    placeholder='Project Description'
                    rows={4}
                    required
                />

                {/* Dates Row */}
                <div className="dates-row">
                    <div className="input-floating">
                        <label>Start Date</label>
                        <DatePicker
                            selected={form.start_date ? new Date(form.start_date) : null}
                            onChange={(date) => {
                                const formattedStartDate = date ? format(date, 'yyyy-MM-dd') : '';
                                handleChange({
                                    target: {
                                        name: 'start_date',
                                        value: formattedStartDate,
                                    },
                                });
                            }}
                            className="datepicker-input"
                            placeholderText="MM/DD/YYYY"
                            dateFormat="MM/dd/yyyy"
                        />
                    </div>

                    <div className="input-floating">
                        <label>End Date</label>
                        <DatePicker
                            selected={form.end_date ? new Date(form.end_date) : null}
                            onChange={(date) => {
                                const formattedEndDate = date ? format(date, 'yyyy-MM-dd') : '';
                                handleChange({
                                    target: {
                                        name: 'end_date',
                                        value: formattedEndDate,
                                    },
                                });
                            }}
                            className="datepicker-input"
                            placeholderText="MM/DD/YYYY"
                            dateFormat="MM/dd/yyyy"
                        />
                    </div>
                </div>

                {/* Status - Using react-select */}
                <div className="small-select">
                    <Select
                        name="status"
                        value={options.find(option => option.value === form.status)}
                        onChange={handleStatusChange}
                        options={options}
                        styles={customStyles}
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                        menuPosition="absolute"
                        menuShouldBlockScroll
                        isSearchable={false}
                        formatOptionLabel={(data, { context }) =>
                            context === 'value' ? `Status: ${data.label}` : data.label
                        }
                    />
                </div>

                <div className="project-budget-with-icon">
                    <i className="fas fa-dollar-sign input-icon"></i> {/* FontAwesome icon */}
                    <input
                        type='number'
                        name='budget'
                        value={form.budget}
                        onChange={handleChange}
                        placeholder='Budget'
                        className='small-select'
                    />
                </div>

                {/* Buttons */}
                <div className="button-group">
                    <button type="submit" className="submit-button">
                        {projectToEdit ? 'Update Project' : 'Create Project'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="cancel-button"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateProjectPage;
